import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { FormApi, ValidationErrors } from 'final-form';
import * as yup from 'yup';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import downloadReceipt from 'utils/downloadReceipt';
import Loader from 'ui/Loader/Loader';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { StatusModal, OTPConfirmationModal, GenerateInvoiceModal } from 'components/modals';
import { AxiosStatus } from 'api/axiosConfig';
import { requiredOnly, validationCallback } from 'utils/validation';
import { changeFieldValue } from 'utils/formMutators';
import { ITransactionField } from 'ui/structure/TransactionCard';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import routePaths from 'routes/routePaths';
import accountsStore from 'store/accountsStore';
import api from 'api/api';
import { onlyNumbersWithDecimals } from 'utils/formatters';
import { IApiResponse } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import { NETWORK_REQUIRED } from 'utils/constants';
import renderGhanaPaymentsFields from './GhanaGovFields';
import {
  IGhanaPaymentsState,
  IFormValues,
  Steps,
  IInvoiceData,
  IGetInvoiceResponse,
  IPayInvoice,
  IServiceResponse,
} from './GhanaGov.model';

import styles from './GhanaGov.module.css';

type IInvoiceModalProps = {
  open: boolean;
  status: 'success' | 'failure';
  content: React.ReactChild;
  onRetry: () => void;
};

const initInvoiceModalProps: IInvoiceModalProps = {
  open: false,
  status: 'failure',
  content: '',
  onRetry: () => null,
};

const mainFormsStore: Record<string, FormApi<IFormValues, Partial<IFormValues>>> = {};

const confirmOrder = [
  'Invoice Number:',
  'Invoice Description:',
  'Created Date:',
  'Expiry Date:',
  'Invoice Status:',
  'Customer Name:',
  'Narration:',
  'Source Account:',
  'Amount:',
];

const transactionDataConfig = {
  invoice: 'Invoice Number:',
  invoiceDescription: 'Invoice Description:',
  createdDate: 'Created Date:',
  expiryDate: 'Expiry Date:',
  invoiceStatus: 'Invoice Status:',
  customerName: 'Customer Name:',
  narration: 'Narration:',
  sourceAccount: 'Source Account:',
  amount: 'Amount:',
};

const GhanaGov = () => {
  const [state, setState] = useState<IGhanaPaymentsState>({
    serviceName: [],
    invoice: '',
    accounts: [],
    currency: '',
  });
  const [step, setStep] = useState(Steps.VERIFY);
  const updateState = useCallback((update: Partial<IGhanaPaymentsState>) => {
    setState((prevState) => ({ ...prevState, ...update }));
  }, []);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [invoiceModalProps, setInvoiceModalProps] = useState<IInvoiceModalProps>(initInvoiceModalProps);
  const [loadingData, setLoadingData] = useState(false);
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [isGenerateInvoiceModalOpen, setGenerateInvoiceModalOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [confirmData, setConfirmData] = useState<ITransactionField[]>([]);
  const [formId] = useState<string>(`${Date.now().toString()}_${Math.random().toString()}`);

  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const mainForm = mainFormsStore[formId]; // TODO re enable

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const openGenerateInvoiceModal = () => setGenerateInvoiceModalOpen(true);
  const closeGenerateInvoiceModal = () => setGenerateInvoiceModalOpen(false);
  const closeAfterGettingInvoice = (form: FormApi<IFormValues, Partial<IFormValues>>, invoiceNumber: string) => {
    setGenerateInvoiceModalOpen(false);
    form.mutators.changeFieldValue('invoice', invoiceNumber);
  };

  const onClickGenerate = () => {
    openGenerateInvoiceModal();
  };

  const schema = yup.object().shape({
    serviceName: requiredOnly('Service name'),
    invoice: requiredOnly('Invoice'),
    invoiceDescription: requiredOnly('Invoice description'),
    createdDate: requiredOnly('date'),
    expiryDate: requiredOnly('Date'),
    invoiceStatus: requiredOnly('Invoice status'),
    customerName: requiredOnly('Customer name'),
    narration: requiredOnly('Narration'),
    sourceAccount: requiredOnly('Source account'),
    amount: requiredOnly('Amount'),
  });

  const validate = useCallback((values: IFormValues) => validationCallback<IFormValues>(schema)(values), [schema]);

  // delete mainForm from mainFormsStore on unmount
  useEffect(
    () => () => {
      delete mainFormsStore[formId];
    },
    [formId],
  );

  const closeStatusModal = useCallback(() => setInvoiceModalProps(initInvoiceModalProps), []);

  const intialValues: IFormValues = useMemo(() => {
    const emptyValues = {
      serviceName: '',
      invoice: '',
      invoiceDescription: '',
      createdDate: '',
      expiryDate: '',
      invoiceStatus: '',
      customerName: '',
      narration: '',
      sourceAccount: '',
      amount: '',
    };

    return emptyValues;
  }, []);

  const { ghsAccounts } = accountsStore;

  useEffect(() => updateState({ accounts: toJS(ghsAccounts) }), [ghsAccounts, updateState]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (ghsAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    const fetchServices = () =>
      api
        .get('billspayment/serviceTypes', {}, true, false)
        .then((response: IServiceResponse) => {
          updateState({ serviceName: response.serviceTypes });
        })
        .catch((error) => {
          console.error(error);
        });

    setLoadingData(true);

    Promise.all([fetchAccounts(), fetchServices()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  const getReceipt = (transactionID: number) =>
    api.get(`/transaction/receipt?trnxID=${transactionID}`).then(downloadReceipt);

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const setValues = (form: FormApi<IFormValues, Partial<IFormValues>>, data: IInvoiceData) => {
    form.mutators.changeFieldValue('invoiceDescription', data.invoiceDescription);
    form.mutators.changeFieldValue('createdDate', data.createdDate);
    form.mutators.changeFieldValue('expiryDate', data.expiryDate);
    form.mutators.changeFieldValue('invoiceStatus', data.invoiceStatus);
    form.mutators.changeFieldValue('customerName', data.customerName);
    form.mutators.changeFieldValue('narration', data.narration);
    form.mutators.changeFieldValue('amount', onlyNumbersWithDecimals(data.totalAmount));
    updateState({ currency: data.totalAmountCurrency });
  };

  const onVerify = useCallback(
    (values: IFormValues, form: FormApi<IFormValues, Partial<IFormValues>>) => {
      if (step === Steps.VERIFY) {
        setSubmitStatus(AxiosStatus.PENDING);

        api
          .get(`billspayment/invoiceLookup?invoiceNumber=${values.invoice}`, {}, true, false)
          .then((data: IGetInvoiceResponse) => {
            setStep(Steps.PROCEED);
            setValues(form, data.invoice);
          })
          .catch(() => {
            setInvoiceModalProps({
              status: 'failure',
              open: true,
              content: 'Invoice number is not verified.',
              onRetry: () => {
                closeStatusModal();
              },
            });
          })
          .finally(() => setSubmitStatus(AxiosStatus.DEFAULT));
      }
    },
    [step, closeStatusModal],
  );
  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
  }, []);

  const onSubmit = useCallback((values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const sourceAccount = ghsAccounts.find((account) => account.accountNumber === values.sourceAccount);
    if (sourceAccount) {
      const transactionAmount = Number(values.amount);
      const availableBalance = Number(sourceAccount.availableBalance);

      if (transactionAmount > availableBalance) {
        setSubmitStatus(AxiosStatus.FAILURE);
        setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
        return;
      }
    }
    api
      .post('/customer/otp/generate')
      .then((resp: IApiResponse) => {
        setSuccessMsg(resp.description);
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });

    const initConfirm = () => {
      setSubmitStatus(AxiosStatus.DEFAULT);
      setConfirmData(getTransactionsArray(values, transactionDataConfig, undefined, confirmOrder));
      openConfirmation();
    };
    initConfirm();
    setSubmitStatus(AxiosStatus.DEFAULT);
  }, []);

  const disableForButton = (hasError: boolean, errors: ValidationErrors) => {
    if (step === Steps.VERIFY) {
      return Boolean(errors?.invoice);
    }
    return hasError;
  };

  const getButtonName = () => {
    if (step === Steps.VERIFY) {
      return 'Verify';
    }
    return 'Proceed';
  };

  const handlerBackClick = () => {
    setStep(Steps.VERIFY);
  };

  const onResend = () => api.post('/customer/otp/generate');
  const onConfirm = useCallback(
    (values: IFormValues, ghanaState: IGhanaPaymentsState) => (otp: string) => {
      setErrorMsg('');
      setSuccessMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      const payInvoice: IPayInvoice = {
        invoiceNumber: values.invoice,
        amount: values.amount,
        currency: ghanaState.currency,
        passwordType: 'OTP',
        password: otp,
        channel: 'MOBILE',
        transType: 'GHANA-GOV',
        sourceAccount: values.sourceAccount,
        serviceName: values.serviceName,
        expiryDate: values.expiryDate,
      };
      // console.log(payInvoice);

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          api
            .post('billspayment/payInvoice', payInvoice)
            .then((data: IApiResponse) => {
              if (data.transactionId) {
                setTransactionId(data.transactionId);
              }
              setSuccessMsg(data.description || 'Bill Payment is successful!');
              setSubmitStatus(AxiosStatus.SUCCESS);
              mainForm?.reset();
            })
            .catch((err: IApiResponse) => {
              setErrorMsg(err.description);
              setSubmitStatus(AxiosStatus.FAILURE);
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [mainForm],
  );

  return (
    <>
      {step === Steps.PROCEED && (
        <NavigateBackStep onClick={handlerBackClick} className={styles.mb20}>
          Back
        </NavigateBackStep>
      )}
      {loadingData && <Loader />}
      {!loadingData && (
        <Form
          onSubmit={onSubmit}
          validate={validate}
          mutators={{ changeFieldValue }}
          initialValues={intialValues}
          render={({ handleSubmit, form, values, errors, hasValidationErrors }) => {
            mainFormsStore[formId] = mainFormsStore[formId] || form;

            return (
              <form onSubmit={handleSubmit}>
                <FieldsLeveler label="Payment details">
                  {renderGhanaPaymentsFields(values, form, state, submitting, step, onClickGenerate)}
                </FieldsLeveler>
                <SubmitLeveler>
                  <Button
                    type={step === Steps.VERIFY ? 'button' : 'submit'}
                    fullWidth
                    disabled={submitting || disableForButton(hasValidationErrors, errors)}
                    onClick={() => onVerify(values, form)}
                  >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : getButtonName()}
                  </Button>
                </SubmitLeveler>
                <OTPConfirmationModal
                  open={isConfirmationOpen}
                  data={confirmData}
                  onClose={closeConfirmation}
                  OTPPinError={OTPError}
                  onResend={onResend}
                  onSubmit={onConfirm(values, state)}
                  submitStatus={submitStatus}
                  onSuccess={handleOnSuccess}
                  onRetry={onRetryConfirm}
                  getReceipt={onGetReceipt}
                  successText={successMsg}
                  failureText={errorMsg}
                />
                <GenerateInvoiceModal
                  open={isGenerateInvoiceModalOpen}
                  onClose={closeGenerateInvoiceModal}
                  onCloseAfterGettingInvoice={(invoiceNumber) => closeAfterGettingInvoice(form, invoiceNumber)}
                  serviceName={values.serviceName}
                  state={state}
                />
              </form>
            );
          }}
        />
      )}
      <StatusModal
        open={invoiceModalProps.open}
        status={invoiceModalProps.status}
        onRetry={invoiceModalProps.onRetry}
        onClose={closeStatusModal}
        onRetryText="Close"
      >
        {invoiceModalProps.content}
      </StatusModal>
    </>
  );
};

export default observer(GhanaGov);
